/** 변동 > 기록 — the change log (S5), the tracker's own entries: a rank that
 *  rose or fell by three or more places, a keyword that entered or left the
 *  ranks, a popularity jump. Was Recent Changes on the old Tracking page. */

import { History } from 'lucide-react'
import type { ChangeEvent } from '@/api/client'
import { useOpenKeyword } from '@/lib/detail'
import { useApps, useChanges } from '@/lib/queries'
import {
  AppIcon,
  CountryTag,
  DataTable,
  Delta,
  EmptyState,
  Shift,
  StatusPill,
  When,
  type Column,
} from '@/ui'

interface Payload {
  term?: string
  country?: string
  rank?: number
  delta?: number
  popularity?: number
  locale?: string
  name?: string
}

function parse(s: string): Payload | null {
  if (!s) return null
  try {
    return JSON.parse(s) as Payload
  } catch {
    return null
  }
}

/** What happened, written from the entry's kind. Rank direction comes from the
 *  kind, never from the sign of `delta`. */
function Description({ event }: { event: ChangeEvent }) {
  const before = parse(event.before_json)
  const after = parse(event.after_json)
  switch (event.kind) {
    case 'rank_up':
    case 'rank_down':
      return (
        <Delta
          status={event.kind}
          delta={Math.abs(after?.delta ?? (before?.rank && after?.rank ? before.rank - after.rank : 0))}
          before={before?.rank}
          after={after?.rank}
        />
      )
    case 'rank_lost':
      return <span className="tabular-nums text-muted-foreground">#{before?.rank ?? '—'}에서 순위 밖</span>
    case 'rank_new':
      return <span className="tabular-nums text-muted-foreground">처음 #{after?.rank ?? '—'}</span>
    case 'popularity_shift':
      return <Shift before={before?.popularity ?? 0} after={after?.popularity ?? 0} />
    case 'metadata':
      return (
        <span className="text-muted-foreground">
          {after?.locale} · {before?.name === after?.name ? '부제·키워드' : `${before?.name} → ${after?.name}`}
        </span>
      )
    default:
      return null
  }
}

export default function ChangeLog() {
  const changes = useChanges()
  const apps = useApps()
  const open = useOpenKeyword()
  const appById = new Map((apps.data?.items ?? []).map((a) => [a.id, a]))

  const appCell = (e: ChangeEvent) => {
    const a = e.app_id ? appById.get(e.app_id) : undefined
    return (
      <span className="inline-flex min-w-0 items-center gap-2">
        <AppIcon src={a?.icon_url} name={a?.name ?? '?'} size={20} />
        <span className="truncate">{a?.name ?? `앱 ${e.app_id ?? '?'}`}</span>
      </span>
    )
  }

  const columns: Column<ChangeEvent>[] = [
    { key: 'kind', header: '종류', width: '110px', card: 'meta', cell: (e) => <StatusPill domain="move" status={e.kind} /> },
    {
      key: 'term',
      header: '키워드',
      card: 'title',
      cell: (e) => {
        const p = parse(e.after_json) ?? parse(e.before_json)
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-medium">{p?.term ?? `#${e.keyword_id ?? ''}`}</span>
            {p?.country && <CountryTag code={p.country} />}
          </span>
        )
      },
    },
    {
      key: 'app',
      header: '앱',
      card: 'hidden',
      cell: (e) => appCell(e),
    },
    {
      // the phone's line under the keyword: the app and when
      key: 'appAt',
      header: '앱 · 시각',
      card: 'sub',
      cardOnly: true,
      cell: (e) => (
        <span className="flex min-w-0 items-center gap-2">
          {appCell(e)}
          <span aria-hidden>·</span>
          <When iso={e.created_at} className="shrink-0 whitespace-nowrap" />
        </span>
      ),
    },
    { key: 'what', header: '변경', card: 'line', cell: (e) => <Description event={e} /> },
    { key: 'at', header: '시각', align: 'right', className: 'whitespace-nowrap text-muted-foreground', card: 'hidden', cell: (e) => <When iso={e.created_at} /> },
  ]

  return (
    <DataTable
      label="변화 기록"
      columns={columns}
      rows={changes.data?.items}
      rowKey={(e) => e.id}
      loading={changes.isLoading}
      error={changes.error}
      onRetry={() => changes.refetch()}
      onRowClick={(e) => {
        if (e.app_id && e.keyword_id) open(e.app_id, e.keyword_id)
      }}
      empty={
        <EmptyState
          icon={History}
          title="변화 기록이 아직 없다"
          description="스냅샷을 돌리고 시간이 지난 뒤 다시 돌리면 순위와 인기도 변화가 쌓인다."
        />
      }
    />
  )
}
